import { Inject, Injectable } from '@angular/core';

import { logger } from 'src/lib/utils/logging';
import { interval, of, Subject } from 'rxjs';
import { concatMap, delay, filter, takeWhile, tap } from 'rxjs/operators';

import { AdobeAnalytics, DigitalData, S, Satellite } from './AdobeAnalytics';
import { ADOBE_ANALYTICS } from './AdobeAnalyticsProvider';
import { AnalyticsEvent } from './model';



@Injectable({providedIn: 'root'})
export class AdobeAnalyticsService {

  private events$ = new Subject<AnalyticsEvent>();
  private queue = [];
  private ready = false;

  constructor(@Inject(ADOBE_ANALYTICS) private adobeAnalytics: AdobeAnalytics) {
    this.events$
      .pipe(concatMap((event: AnalyticsEvent) => of(event).pipe(delay(event.delay))))
      .subscribe((event: AnalyticsEvent) => this.send(event));

    this.checkIfReady(1000, 20);
  }

  track(event: AnalyticsEvent): void {
    if (this.ready) {
      logger.log('[ AdobeAnalyticsService ] logEvent : track', event);
      this.events$.next(event);
    }
    else {
      logger.log('[ AdobeAnalyticsService ] logEvent : enqueue', event);
      this.queue.push(event);
    }
  }

  private nextEvent(): void {
    if (this.queue.length) {
      this.events$.next(this.queue.pop());
    }
  }

  private send(event: AnalyticsEvent) {
    try {
      if (event.pageName) {
        this.pageName = event.pageName;
      }

      this.reason = event.description;
      this.vars = 'prop1,events';
      this.satellite.track(event.type);

      logger.log('[ AdobeAnalyticsService ] send :', event);

      this.nextEvent();
    }
    catch (error) {
      logger.error('[ AdobeAnalyticsService ] send :', event, error);
    }
  }


  //=============================================
  //  Ready state
  //---------------------------------------------

  private checkIfReady(period: number, maxAttempts: number): void {
    interval(period).pipe(
      tap(value => logger.log('[ AdobeAnalyticsService ] checking if ready :', value + 1, '/', maxAttempts)),
      takeWhile(value => value < maxAttempts && !this.isReady(), true),
      filter(() => this.isReady())
    ).subscribe(() => {
      logger.log('[ AdobeAnalyticsService ] ready');
      this.ready = true;
      this.nextEvent();
    });
  }

  private isReady(): boolean {
    return !!(this.digitalData && this.s && this.satellite);
  }


  //=============================================
  //  Getters and setters
  //---------------------------------------------

  private get pageName(): string {
    try {
      return this.digitalData.page.pageInfo.pageName;
    }
    catch (error) {
      logger.warn('[ AdobeAnalyticsService ] get pageName :', error);
      return '';
    }
  }

  private set pageName(value: string) {
    try {
      this.digitalData.page.pageInfo.pageName = value;
    }
    catch (error) {
      logger.warn(`[ AdobeAnalyticsService ] set pageName '${value}' :`, error);
    }
  }

  private set reason(value: string) {
    try {
      this.digitalData.page.pageInfo.why = value;
    }
    catch (error) {
      logger.warn(`[ AdobeAnalyticsService ] set reason '${value}' :`, error);
    }
  }

  private set vars(value: string) {
    try {
      this.s.linkTrackVars = value;
    }
    catch (error) {
      logger.warn(`[ AdobeAnalyticsService ] set vars '${value}' :`, error);
    }
  }

  private get digitalData(): DigitalData {
    return this.adobeAnalytics.digitalData;
  }

  private get s(): S {
    return this.adobeAnalytics.s;
  }

  private get satellite(): Satellite {
    return this.adobeAnalytics._satellite;
  }

}
